import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EMPLOYEE_ROLES, type EmployeeRole } from "@care-by-ella/contracts";
import { createStaff } from "@/lib/staff-api";

// TES-491 — staff onboarding. Was "Ny läkare": no role field, and the service
// call omitted `role` entirely so the column took its 'doctor' default. The
// portal therefore could not create a coach at all, which is why every coach
// on the box got there via dev-auth's find-or-create instead of being
// provisioned. Now the role is chosen, and the form posts to the admin-only
// staff endpoint.
//
// CAR-55 — onboarding form. The minimum a new doctor needs to be
// resolvable by BankID is PN + tenant; displayName + email are for the
// inbox/meeting roster + transactional comms. An empty schedule is
// fine — the doctor fills in her weeks later once availability matters.
//
// Server-side validation is authoritative; client-side checks are here
// for fast feedback. The PN check matches the API's Zod regex (12
// digits, plausible birth year) so a valid local entry never round-trips
// to a 400.

function validatePnLocal(pn: string): string | null {
  if (!/^\d{12}$/.test(pn))
    return "Personnumret måste vara 12 siffror (ÅÅÅÅMMDDXXXX).";
  const year = Number(pn.slice(0, 4));
  const now = new Date().getFullYear();
  if (year < 1900 || year > now) return "Födelseåret verkar inte stämma.";
  return null;
}

const ROLE_LABEL: Record<EmployeeRole, string> = {
  doctor: "Läkare",
  coach: "Coach",
  admin: "Administratör",
};

const ROLE_HELP: Record<EmployeeRole, string> = {
  doctor: "Ser den kliniska vyn: beslutskö, patienter, möten och recept.",
  coach: "Ser coachens arbetsyta: arbetskö, chatt och coachmaterial.",
  admin: "Ser driftvyn: personal, innehåll, formulär och inställningar.",
};

const ERROR_COPY: Record<string, string> = {
  validation_failed:
    "Något i formuläret stämmer inte. Kontrollera fälten och försök igen.",
  clinician_duplicate:
    "Någon med samma personnummer eller e-post finns redan i kliniken.",
  missing_partner_clinic:
    "Din inloggning saknar koppling till en klinik — kontakta support.",
};

export function NewClinicianRoute() {
  const nav = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [personalNumber, setPersonalNumber] = useState("");
  const [role, setRole] = useState<EmployeeRole>("doctor");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canSubmit =
    !submitting &&
    displayName.trim().length > 0 &&
    /\S+@\S+\.\S+/.test(email) &&
    validatePnLocal(personalNumber) === null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await createStaff({
        displayName: displayName.trim(),
        email: email.trim().toLowerCase(),
        personalNumber,
        role,
      });
      setSuccess(
        `${result.displayName} är nu upplagd som ${ROLE_LABEL[
          result.role
        ].toLowerCase()}. Hen kan logga in via BankID.`,
      );
      setDisplayName("");
      setEmail("");
      setPersonalNumber("");
      setRole("doctor");
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(ERROR_COPY[code] ?? `Något gick fel (${code}).`);
    } finally {
      setSubmitting(false);
    }
  }

  const pnLocalError =
    personalNumber.length > 0 ? validatePnLocal(personalNumber) : null;

  return (
    <div className="flex flex-col gap-md max-w-xl">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          Ny personal
        </h1>
        <p className="text-textSecondary text-sm">
          Lägg till en kollega så att hen kan logga in via BankID. Rollen
          bestämmer vilken vy hen möter — den går att ändra senare i
          personallistan.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-md">
        <label className="flex flex-col gap-xs">
          <span className="text-sm font-medium">Namn</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            autoComplete="name"
            className="border border-surfaceMuted rounded-md px-md py-sm bg-surface"
            placeholder="Förnamn Efternamn"
          />
        </label>

        {/* The role is the consequential field on this form — it decides which
            of the three portals the person lands in — so it sits with the
            identity fields, not hidden behind a default. */}
        <label className="flex flex-col gap-xs">
          <span className="text-sm font-medium">Roll</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as EmployeeRole)}
            className="border border-surfaceMuted rounded-md px-md py-sm bg-surface"
          >
            {EMPLOYEE_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          <span className="text-xs text-textSecondary">{ROLE_HELP[role]}</span>
        </label>

        <label className="flex flex-col gap-xs">
          <span className="text-sm font-medium">E-post</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="border border-surfaceMuted rounded-md px-md py-sm bg-surface"
            placeholder="namn@kliniken.se"
          />
        </label>

        <label className="flex flex-col gap-xs">
          <span className="text-sm font-medium">Personnummer</span>
          <input
            type="text"
            inputMode="numeric"
            value={personalNumber}
            onChange={(e) =>
              setPersonalNumber(e.target.value.replace(/\D/g, "").slice(0, 12))
            }
            required
            maxLength={12}
            className="border border-surfaceMuted rounded-md px-md py-sm bg-surface font-mono"
            placeholder="ÅÅÅÅMMDDXXXX"
          />
          <span className="text-xs text-textSecondary">
            Tolv siffror utan bindestreck. Detta är vad BankID matchar mot vid
            inloggning.
          </span>
          {pnLocalError ? (
            <span className="text-xs text-danger">{pnLocalError}</span>
          ) : null}
        </label>

        {error ? (
          <div className="border border-danger rounded-md p-sm text-sm text-danger bg-surface">
            {error}
          </div>
        ) : null}
        {success ? (
          <div className="border border-success rounded-md p-sm text-sm bg-surface">
            {success}
          </div>
        ) : null}

        <div className="flex gap-sm">
          <button
            type="submit"
            disabled={!canSubmit}
            className="bg-brand text-white px-md py-sm rounded-md font-medium disabled:opacity-50"
          >
            {submitting ? "Skapar…" : "Lägg till personal"}
          </button>
          <button
            type="button"
            onClick={() => nav("/staff")}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Avbryt
          </button>
        </div>
      </form>
    </div>
  );
}
